import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Building2, Snowflake, Sun, FlaskConical, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-border/60 p-3">
      <div className="text-[10px] uppercase font-mono tracking-wider text-muted-foreground">{label}</div>
      <div className="metric text-lg mt-0.5">{value}</div>
    </div>
  );
}

export function OpsOrgBrowser({ demoOnly = false }) {
  const [q, setQ] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/ops/orgs", { params: q ? { q } : {} });
    setOrgs(demoOnly ? data.filter((o) => o.is_demo) : data);
  };
  const openOrg = async (id) => { const { data } = await api.get(`/ops/orgs/${id}`); setSel(data); };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  const createDemo = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ops/orgs/demo"); toast.success(`Created ${data.name} — ${data.contacts} sandbox contacts, live send off`); await load(); await openOrg(data.org_id); }
    catch { toast.error("Could not create demo org"); } finally { setBusy(false); }
  };
  const expire = async (id) => { await api.post(`/ops/orgs/${id}/expire`); toast.success("Demo expired — plan cleared, live send off"); await load(); await openOrg(id); };
  const freeze = async (id, frozen) => { await api.post(`/ops/orgs/${id}/freeze`, { frozen }); toast.success(frozen ? "Send frozen" : "Send unfrozen"); await load(); await openOrg(id); };
  const setFlag = async (id, key, val) => { const { data } = await api.put(`/ops/orgs/${id}/flags`, { [key]: val }); setSel({ ...sel, feature_flags: data.feature_flags }); toast.success("Flag updated"); };

  return (
    <div className="grid lg:grid-cols-[1fr_1.3fr] gap-6">
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by org name or owner email" className="pl-9" data-testid="ops-search" />
          </div>
          <Button className="rounded-md bg-amber-500 text-black hover:bg-amber-400 shrink-0" onClick={createDemo} disabled={busy} data-testid="ops-create-demo"><FlaskConical className="h-4 w-4 mr-1.5" /> Demo org</Button>
        </div>
        <div className="space-y-2" data-testid="ops-org-list">
          {orgs.length === 0 ? <p className="text-sm text-muted-foreground p-3">{demoOnly ? "No demo orgs yet." : "No organizations found."}</p> : orgs.map((o) => (
            <button key={o.id} onClick={() => openOrg(o.id)} data-testid={`ops-org-${o.id}`}
              className={`w-full text-left rounded-lg border p-3 transition-colors ${sel?.id === o.id ? "border-amber-500/60 bg-amber-500/5" : "border-border/60 hover:bg-accent/40"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 min-w-0"><Building2 className="h-4 w-4 text-muted-foreground shrink-0" /><span className="font-medium truncate">{o.name}</span></span>
                <div className="flex items-center gap-1 shrink-0">
                  {o.is_demo && <Badge variant="outline" className="text-[10px]">demo</Badge>}
                  <Badge className={o.live_send ? "bg-emerald-500/15 text-emerald-500 border-0 text-[10px]" : "bg-muted text-muted-foreground border-0 text-[10px]"}>{o.live_send ? "live" : "no send"}</Badge>
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-1 truncate">{o.owner_email || "no owner"} · {o.plan_id || "no plan"}</div>
            </button>
          ))}
        </div>
      </div>

      {sel ? (
        <div className="rounded-xl border border-border/70 bg-card/50 p-5 h-fit" data-testid="ops-org-detail">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-xl font-semibold">{sel.name}</h2>
              <p className="text-xs text-muted-foreground">{sel.owner_email || "no owner"}</p>
            </div>
            <Badge className={sel.live_send ? "bg-emerald-500/15 text-emerald-500 border-0" : "bg-amber-500/15 text-amber-500 border-0"} data-testid="ops-livesend">Live send {sel.live_send ? "on" : "off"}</Badge>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-5">
            <Stat label="Plan" value={sel.plan_name || "none"} />
            <Stat label="Workspaces" value={sel.workspaces} />
            <Stat label="Contacts" value={sel.usage.contacts} />
            <Stat label="Sends" value={sel.usage.sends} />
            <Stat label="AI credits" value={sel.usage.ai_credits} />
            <Stat label="Frozen" value={sel.send_frozen ? "yes" : "no"} />
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            <Button size="sm" variant="outline" className="rounded-md" onClick={() => freeze(sel.id, !sel.send_frozen)} data-testid="ops-freeze">
              {sel.send_frozen ? <><Sun className="h-3.5 w-3.5 mr-1" /> Unfreeze send</> : <><Snowflake className="h-3.5 w-3.5 mr-1" /> Freeze send</>}
            </Button>
            <Button size="sm" variant="outline" className="rounded-md" onClick={() => expire(sel.id)} data-testid="ops-expire"><Zap className="h-3.5 w-3.5 mr-1" /> Expire demo</Button>
          </div>

          <div className="rounded-lg border border-border/60 p-4 mb-5">
            <div className="eyebrow mb-3">Feature flags</div>
            {["public_sector", "hubspot_card", "navigator_import"].map((k) => (
              <div key={k} className="flex items-center justify-between py-1.5">
                <span className="text-sm capitalize">{k.replace(/_/g, " ")}</span>
                <Switch checked={!!sel.feature_flags[k]} onCheckedChange={(v) => setFlag(sel.id, k, v)} data-testid={`ops-flag-${k}`} />
              </div>
            ))}
          </div>

          <div className="eyebrow mb-2">Last audit rows</div>
          <div className="space-y-1 max-h-52 overflow-y-auto" data-testid="ops-audit">
            {sel.audit.length === 0 ? <p className="text-xs text-muted-foreground">No audit rows.</p> : sel.audit.map((r, i) => (
              <div key={`${r.at}-${i}`} className="text-xs flex items-center gap-2">
                <span className="text-muted-foreground font-mono whitespace-nowrap">{new Date(r.at).toLocaleString()}</span>
                <span className="text-amber-500 font-mono">{r.action}</span>
                <span className="text-muted-foreground truncate">{r.target}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border/60 flex items-center justify-center text-sm text-muted-foreground h-64" data-testid="ops-empty">Select an org to inspect</div>
      )}
    </div>
  );
}
